import axios from 'axios';
import ReactDOM from 'react-dom/client';
import { message as MessageUI } from 'antd';
import { localize } from 'locales';
import { PublicClientApplication } from '@azure/msal-browser';
import { getToken, setToken, cleanCache } from '../../utils/token-kit';
import { loginRequest, msalConfig } from 'auth-config';
import loadingIcon from 'assets/images/loading-unscreen.gif';

const baseURL = process.env.REACT_APP_URL;

function isRequestSuccess(response) {
	return response.message === 'success' || response.status === 200 || response.statusText === 'OK';
}

function getResponseData(response) {
	return response.responseData;
}

/// >>>>> request client
const client = axios.create({
	// base_url
	baseURL: baseURL,
	// request timeout
	timeout: 1000000,
});
let loadingRequestCount = 0;

const silentLoadingPaths = ['/image/view'];

function _showLoadingModal() {
	if (loadingRequestCount === 0) {
		const dom = document.createElement('div');
		dom.setAttribute('id', 'loading');
		document.body.appendChild(dom);
		const root = ReactDOM.createRoot(dom);
		root.render(
			<div
				className="loading-word-draw-con"
				style={{
					position: 'fixed',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					zIndex: 10000,
					backgroundColor: 'rgba(255, 255, 255, 0.5)',
					textAlign: 'center',
				}}
			>
				<div className="loading-word-draw-icon">
					<img src={loadingIcon} alt="Loading" style={{ width: '50px', height: '50px', paddingLeft: 1 }} />
				</div>
				<div
					style={{
						color: '#000',
						fontSize: '12px',
						marginTop: '-5px',
						fontFamily: 'sans-serif',
						textAlign: 'center',
					}}
				>
					Loading...
				</div>
			</div>,
		);
	}
	loadingRequestCount++;
}

function _dismissLoadingModal() {
	loadingRequestCount--;
	if (loadingRequestCount === 0) {
		document.body.removeChild(document.getElementById('loading'));
	}
}

function _navToLoginPage() {
	cleanCache();
	const locals = `${window.location.origin}/login`;
	window.location.href = locals;
}
async function refreshToken(requestConfig) {
	const instance = await PublicClientApplication.createPublicClientApplication(msalConfig);
	const account = instance.getAllAccounts()[0];
	if (!account) {
		_dismissLoadingModal();
		return;
	}
	const accessTokenRequest = {
		scopes: loginRequest.scopes,
		account: account,
		forceRefresh: true,
	};

	try {
		await instance.initialize();
		const res = await instance.acquireTokenSilent(accessTokenRequest);
		console.log('>>>>refreshed token: ' + res.accessToken);
		setToken(res.accessToken);
		const response = await client(requestConfig);
		_dismissLoadingModal();
		if (!isRequestSuccess(response)) {
			_navToLoginPage();
			return Promise.resolve(true);
		}
		return Promise.resolve(response);
	} catch (error) {
		_dismissLoadingModal();
		_navToLoginPage();
		return Promise.resolve(true);
	}
}

client.interceptors.request.use(
	(config) => {
		if (
			silentLoadingPaths.indexOf(config?.url) < 0 &&
			!config.url?.includes('/download') &&
			!config.url?.includes('/upload')
		) {
			_showLoadingModal();
		}
		const noAuthUrls = ['sso/login', 'refreshToken'];
		if (!noAuthUrls?.includes(config.url)) {
			const autoToken = getToken();
			if (autoToken) {
				config.headers['Authorization'] = 'Bearer ' + autoToken;
			}
		}
		return config;
	},
	(error) => {
		_dismissLoadingModal();
		return Promise.reject(error);
	},
);

client.interceptors.response.use(
	(response) => {
		setTimeout(() => {
			if (silentLoadingPaths.indexOf(response.config.url) < 0 && !response.config.url.includes('/download')) {
				_dismissLoadingModal();
			}
		}, 1000);
		return response.data;
	},
	async function (error) {
		if (error?.response?.status === 401) {
			return await refreshToken(error.config);
		} else {
			MessageUI.error(localize('requestFailed'));
		}
		_dismissLoadingModal();
		return Promise.reject(error);
	},
);

export { isRequestSuccess, getResponseData };
export default client;
